import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { platformAPI } from '../../../services/platformAPI';
import { activeImportChoices, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';

const FALLBACK_SOURCES = [
  { value: 'employee', label: 'Employee' },
  { value: 'associate', label: 'Associate' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'jv_nominee', label: 'JV nominee' }
];

const emptySlot = {
  designationCode: '',
  qualificationRequirement: '',
  minTotalExperienceYears: '',
  minRelevantExperienceYears: '',
  deploymentFrom: '',
  deploymentTo: '',
  effortQuantity: '',
  effortUnit: '',
  cvRequired: true,
  evidenceRequired: true,
  consentRequired: true,
  sourcePage: ''
};

const dateLabel = (value) => (value ? String(value).slice(0, 10) : 'Blank');

const TeamResourcesPanel = ({ tenderId, canEdit }) => {
  const [sheet, setSheet] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [error, setError] = useState('');
  const [slot, setSlot] = useState(emptySlot);
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState({});
  const [requirements, setRequirements] = useState({});
  const [decisionComment, setDecisionComment] = useState('');
  const [staffingSources, setStaffingSources] = useState(FALLBACK_SOURCES);

  const load = async () => {
    const response = await platformAPI.getStaffing(tenderId);
    setSheet(response.data?.data || null);
  };

  useEffect(() => {
    let live = true;
    loadOrganisationSettings().then((pack) => {
      if (!live) return;
      const next = activeImportChoices(pack?.settings?.importLists?.staffingSources);
      setStaffingSources(next.length ? next : FALLBACK_SOURCES);
    });
    platformAPI.listReference('personnel-designations').then((response) => {
      if (live) setDesignations(response.data?.data?.values || response.data?.data || []);
    }).catch(() => {});
    load().catch((err) => setError(err.response?.data?.message || 'The team sheet could not be loaded.'));
    return () => { live = false; };
  }, [tenderId]);

  const saveSlot = async (event) => {
    event.preventDefault();
    setError('');
    try {
      await platformAPI.saveRoleSlot(tenderId, { ...slot, idempotencyKey: `${tenderId}-${Date.now()}` });
      setSlot(emptySlot);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The role slot was not saved.');
    }
  };

  const search = async (event) => {
    event.preventDefault();
    const response = await platformAPI.searchStaffing(tenderId, { q: query });
    setPeople(response.data?.data || []);
  };

  const choose = async (slotId) => {
    const row = pick[slotId] || {};
    setError('');
    try {
      await platformAPI.selectRolePerson(tenderId, slotId, {
        personnelId: row.personnelId,
        source: row.source || 'employee',
        partnerId: row.partnerId || undefined,
        cvDocumentId: row.cvDocumentId || undefined,
        cvDocumentVersion: row.cvDocumentVersion || undefined,
        assignmentEvidenceIds: row.evidenceId ? [row.evidenceId] : [],
        selectionRationale: row.rationale || ''
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The person was not selected.');
    }
  };

  const recordConsent = async (proposalId, slotId) => {
    const row = pick[slotId] || {};
    setError('');
    try {
      await platformAPI.recordStaffConsent(tenderId, proposalId, {
        consentDocumentId: row.consentDocumentId,
        validFrom: row.validFrom || undefined,
        validTo: row.validTo || undefined
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The consent was not recorded.');
    }
  };

  const reviewConsent = async (proposalId) => {
    setError('');
    try {
      await platformAPI.reviewStaffConsent(tenderId, proposalId);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The consent was not reviewed.');
    }
  };

  const saveRequirements = async (row) => {
    const draft = requirements[row._id] || {};
    setError('');
    try {
      await platformAPI.updateRoleSlot(tenderId, row._id, {
        qualificationRequirement: draft.qualificationRequirement ?? row.qualificationRequirement ?? '',
        minTotalExperienceYears: draft.minTotalExperienceYears ?? (row.minTotalExperienceYears ?? ''),
        minRelevantExperienceYears: draft.minRelevantExperienceYears ?? (row.minRelevantExperienceYears ?? ''),
        deploymentFrom: draft.deploymentFrom ?? (row.deploymentFrom ? String(row.deploymentFrom).slice(0, 10) : ''),
        deploymentTo: draft.deploymentTo ?? (row.deploymentTo ? String(row.deploymentTo).slice(0, 10) : ''),
        effortQuantity: draft.effortQuantity ?? (row.effortQuantity ?? ''),
        effortUnit: draft.effortUnit ?? (row.effortUnit || '')
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The role requirement was not saved.');
    }
  };

  const assess = async (proposalId, slotId, kind) => {
    const row = pick[slotId] || {};
    setError('');
    try {
      await platformAPI.saveStaffAssessment(tenderId, proposalId, {
        kind,
        result: row[`${kind}Result`] || '',
        comments: row[`${kind}Comments`] || '',
        evidenceDocumentId: row[`${kind}Evidence`] || undefined
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The assessment was not saved.');
    }
  };

  const submitTeam = async () => {
    setError('');
    try {
      await platformAPI.submitTeam(tenderId, { idempotencyKey: `submit-${tenderId}-${sheet?.version?.current?.versionNumber || 1}` });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The team was not submitted.');
    }
  };

  const decide = async (action) => {
    setError('');
    try {
      await platformAPI.decideTeam(tenderId, {
        action,
        comment: decisionComment,
        idempotencyKey: `${action}-${tenderId}-${sheet?.version?.current?.versionNumber || 1}`
      });
      setDecisionComment('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The team decision was not recorded.');
    }
  };

  const override = async (proposalId, slotId) => {
    const row = pick[slotId] || {};
    setError('');
    try {
      await platformAPI.overrideStaffConflict(tenderId, proposalId, { reason: row.overrideReason || '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'The override was not recorded.');
    }
  };

  if (!sheet) return <Spinner animation="border" size="sm" />;
  const values = Array.isArray(designations) ? designations : [];
  const current = sheet.version?.current;
  const history = sheet.version?.history || [];
  const earlierApproval = history.filter((row) => row.status === 'approved');
  const canSubmit = canEdit && (!current || current.status === 'draft' || current.status === 'returned');

  return (
    <div className="bg-register">
      <p className="bg-register__note">Role slots, named people, and the current team version. A submitted or approved version stays as recorded. A later edit opens a new draft. This does not set a rate.</p>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      <Alert variant={current?.usable ? 'light' : 'warning'}>
        {current
          ? `Current team version ${current.versionNumber} is ${current.status}${current.usable ? ' and is the approved team.' : ' and is not the approved team.'}`
          : 'No team version is recorded yet.'}
        {current?.decisionComment ? ` Decision comment: ${current.decisionComment}` : ''}
        {earlierApproval.length ? ` Earlier approved ${earlierApproval.map((row) => `version ${row.versionNumber}`).join(', ')} remains history and is not current.` : ''}
        {current && !current.usable && earlierApproval.length ? ' This revision requires review before the team can be used.' : ''}
      </Alert>
      {history.length ? (
        <p className="text-muted">
          History: {history.map((row) => `v${row.versionNumber} ${row.status}${row.decisionComment ? ` (${row.decisionComment})` : ''}`).join(' · ')}
        </p>
      ) : null}
      {(sheet.blockers || []).length ? (
        <Alert variant="warning">
          Submission is blocked: {(sheet.blockers || []).map((item) => `${item.slotCode ? `${item.slotCode}: ` : ''}${item.label}`).join(' · ')}
        </Alert>
      ) : <Alert variant="light">No submission blockers on the slots recorded so far.</Alert>}
      {(sheet.legacyProposals || []).length ? (
        <Alert variant="warning">
          {sheet.legacyProposals.map((row) => `${row.personName || 'Named person'} (${row.proposedRole || 'role not stored'}) needs a role-slot mapping.`).join(' ')}
        </Alert>
      ) : null}
      <Table size="sm" responsive>
        <thead>
          <tr>
            <th>Slot</th>
            <th>Requirement</th>
            <th>Person</th>
            <th>Consent</th>
            <th>Availability</th>
          </tr>
        </thead>
        <tbody>
          {(sheet.slots || []).map((row) => (
            <tr key={row._id}>
              <td>{row.slotCode}<div className="text-muted">{row.designationLabel}</div></td>
              <td>
                <div>{row.qualificationRequirement || 'Qualification blank'}</div>
                <div className="text-muted">
                  Qualification assessment {row.proposal?.qualificationAssessment?.result || 'not recorded'}
                  {row.proposal?.qualificationAssessment?.comments ? ` · ${row.proposal.qualificationAssessment.comments}` : ''}
                </div>
                <div className="text-muted">
                  Experience assessment {row.proposal?.experienceAssessment?.result || 'not recorded'}
                  {row.proposal?.experienceAssessment?.comments ? ` · ${row.proposal.experienceAssessment.comments}` : ''}
                </div>
                <div className="text-muted">
                  Total {row.minTotalExperienceYears ?? 'blank'} · Relevant {row.minRelevantExperienceYears ?? 'blank'}
                  {' · '}{dateLabel(row.deploymentFrom)} to {dateLabel(row.deploymentTo)}
                  {' · '}{row.effortQuantity ?? 'blank'} {row.effortUnit || ''}
                </div>
                {(row.missingFacts || []).length ? <div>{row.missingFacts.join('; ')}</div> : null}
              </td>
              <td>
                {row.person ? (
                  <div>
                    {row.person.name}
                    <div className="text-muted">{row.proposal?.source || 'Source blank'}{row.proposal?.pinnedCvVersion ? ` · pinned CV ${row.proposal.pinnedCvVersion}` : ''}</div>
                    <div className="text-muted">{row.proposal?.selectionRationale || 'Rationale blank'}</div>
                  </div>
                ) : 'Unfilled'}
              </td>
              <td>{row.consent?.label}</td>
              <td>
                {row.conflict?.label}
                {(row.conflict?.overlaps || []).map((item, index) => (
                  <div key={index} className="text-muted">{item.kind} {item.reference || item.slotCode || ''} {item.from || 'blank'} to {item.to || 'blank'}</div>
                ))}
                {row.conflict?.overrideReason ? <div>Override: {row.conflict.overrideReason}</div> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {canEdit ? (
        <>
          <form className="bg-register__form" onSubmit={saveSlot}>
            <section>
              <h2>Role slot</h2>
              <div className="bg-register__grid">
                <Form.Group className="bg-register__span-4">
                  <Form.Label>Role</Form.Label>
                  <Form.Select aria-label="Role designation" value={slot.designationCode} onChange={(event) => setSlot({ ...slot, designationCode: event.target.value })}>
                    <option value="">Select</option>
                    {values.map((item) => <option key={item.code || item._id} value={item.code}>{item.label}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="bg-register__span-8">
                  <Form.Label>Required qualification</Form.Label>
                  <Form.Control aria-label="Required qualification" value={slot.qualificationRequirement} onChange={(event) => setSlot({ ...slot, qualificationRequirement: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Minimum total experience</Form.Label>
                  <Form.Control aria-label="Minimum total experience" value={slot.minTotalExperienceYears} onChange={(event) => setSlot({ ...slot, minTotalExperienceYears: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Minimum relevant experience</Form.Label>
                  <Form.Control aria-label="Minimum relevant experience" value={slot.minRelevantExperienceYears} onChange={(event) => setSlot({ ...slot, minRelevantExperienceYears: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Deployment from</Form.Label>
                  <Form.Control type="date" aria-label="Deployment from" value={slot.deploymentFrom} onChange={(event) => setSlot({ ...slot, deploymentFrom: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Deployment to</Form.Label>
                  <Form.Control type="date" aria-label="Deployment to" value={slot.deploymentTo} onChange={(event) => setSlot({ ...slot, deploymentTo: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Effort</Form.Label>
                  <Form.Control aria-label="Expected effort" value={slot.effortQuantity} onChange={(event) => setSlot({ ...slot, effortQuantity: event.target.value })} />
                </Form.Group>
                <Form.Group className="bg-register__span-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select aria-label="Effort unit" value={slot.effortUnit} onChange={(event) => setSlot({ ...slot, effortUnit: event.target.value })}>
                    <option value="">Select</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="bg-register__span-6">
                  <Form.Label>Source page</Form.Label>
                  <Form.Control aria-label="Source page" value={slot.sourcePage} onChange={(event) => setSlot({ ...slot, sourcePage: event.target.value })} />
                </Form.Group>
                <Form.Check className="bg-register__span-4" type="checkbox" label="CV required" checked={slot.cvRequired} onChange={(event) => setSlot({ ...slot, cvRequired: event.target.checked })} />
                <Form.Check className="bg-register__span-4" type="checkbox" label="Evidence required" checked={slot.evidenceRequired} onChange={(event) => setSlot({ ...slot, evidenceRequired: event.target.checked })} />
                <Form.Check className="bg-register__span-4" type="checkbox" label="Consent required" checked={slot.consentRequired} onChange={(event) => setSlot({ ...slot, consentRequired: event.target.checked })} />
              </div>
              <Button type="submit" size="sm" disabled={!slot.designationCode}>Add role slot</Button>
            </section>
          </form>
          <form className="bg-register__form" onSubmit={search}>
            <section>
              <h2>Find a person</h2>
              <div className="bg-register__pair">
                <Form.Control aria-label="Personnel search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, qualification, or experience" />
                <Button type="submit" size="sm">Search</Button>
              </div>
              {people.length ? (
                <ul className="small mt-2">
                  {people.map((person) => (
                    <li key={person._id}>{person.name} · {person.designation || 'Designation blank'} · {(person.missingFacts || []).join(', ') || 'Structured facts present'}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          </form>
          {(sheet.slots || []).map((row) => (
            <form key={`edit-${row._id}`} className="bg-register__form" onSubmit={(event) => { event.preventDefault(); choose(row._id); }}>
              <section>
                <h2>{row.slotCode}</h2>
                <div className="bg-register__grid">
                  <Form.Group className="bg-register__span-8">
                    <Form.Label>Required qualification</Form.Label>
                    <Form.Control aria-label={`Qualification for ${row.slotCode}`} value={requirements[row._id]?.qualificationRequirement ?? row.qualificationRequirement ?? ''} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], qualificationRequirement: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-2">
                    <Form.Label>Total experience</Form.Label>
                    <Form.Control aria-label={`Total experience for ${row.slotCode}`} value={requirements[row._id]?.minTotalExperienceYears ?? (row.minTotalExperienceYears ?? '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], minTotalExperienceYears: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-2">
                    <Form.Label>Relevant experience</Form.Label>
                    <Form.Control aria-label={`Relevant experience for ${row.slotCode}`} value={requirements[row._id]?.minRelevantExperienceYears ?? (row.minRelevantExperienceYears ?? '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], minRelevantExperienceYears: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-3">
                    <Form.Label>Deployment from</Form.Label>
                    <Form.Control type="date" aria-label={`Deployment from ${row.slotCode}`} value={requirements[row._id]?.deploymentFrom ?? (row.deploymentFrom ? String(row.deploymentFrom).slice(0, 10) : '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], deploymentFrom: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-3">
                    <Form.Label>Deployment to</Form.Label>
                    <Form.Control type="date" aria-label={`Deployment to ${row.slotCode}`} value={requirements[row._id]?.deploymentTo ?? (row.deploymentTo ? String(row.deploymentTo).slice(0, 10) : '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], deploymentTo: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-3">
                    <Form.Label>Effort</Form.Label>
                    <Form.Control aria-label={`Effort for ${row.slotCode}`} value={requirements[row._id]?.effortQuantity ?? (row.effortQuantity ?? '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], effortQuantity: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-3">
                    <Form.Label>Unit</Form.Label>
                    <Form.Select aria-label={`Effort unit for ${row.slotCode}`} value={requirements[row._id]?.effortUnit ?? (row.effortUnit || '')} onChange={(event) => setRequirements((current) => ({ ...current, [row._id]: { ...current[row._id], effortUnit: event.target.value } }))}>
                      <option value="">Select</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <Button size="sm" type="button" className="mb-3" onClick={() => saveRequirements(row)}>Save requirements</Button>
                <div className="bg-register__grid">
                  <Form.Group className="bg-register__span-4">
                    <Form.Label>Person</Form.Label>
                    <Form.Select aria-label={`Person for ${row.slotCode}`} value={pick[row._id]?.personnelId || ''} onChange={(event) => {
                      const person = people.find((item) => item._id === event.target.value);
                      setPick((current) => ({ ...current, [row._id]: { ...current[row._id], personnelId: event.target.value, cvDocumentId: person?.cvDocumentId || current[row._id]?.cvDocumentId || '', cvDocumentVersion: person?.cvVersion || '' } }));
                    }}>
                      <option value="">Select</option>
                      {people.map((person) => <option key={person._id} value={person._id}>{person.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="bg-register__span-4">
                    <Form.Label>Source</Form.Label>
                    <Form.Select aria-label={`Source for ${row.slotCode}`} value={pick[row._id]?.source || staffingSources[0]?.value || 'employee'} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], source: event.target.value } }))}>
                      {keepCurrent(staffingSources.map((item) => item.value), pick[row._id]?.source, staffingSources.map((item) => item.value)).map((code) => {
                        const item = staffingSources.find((rowItem) => rowItem.value === code) || FALLBACK_SOURCES.find((rowItem) => rowItem.value === code);
                        return <option key={code} value={code}>{item?.label || code}</option>;
                      })}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="bg-register__span-4">
                    <Form.Label>CV</Form.Label>
                    <Form.Select aria-label={`CV for ${row.slotCode}`} value={pick[row._id]?.cvDocumentId || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], cvDocumentId: event.target.value } }))}>
                      <option value="">Select</option>
                      {(sheet.documents || []).map((doc) => <option key={doc._id} value={doc._id}>{doc.name} · v{doc.version || '—'}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="bg-register__span-6">
                    <Form.Label>Selection rationale</Form.Label>
                    <Form.Control aria-label={`Rationale for ${row.slotCode}`} value={pick[row._id]?.rationale || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], rationale: event.target.value } }))} />
                  </Form.Group>
                  <Form.Group className="bg-register__span-6">
                    <Form.Label>Assignment evidence</Form.Label>
                    <Form.Select aria-label={`Evidence for ${row.slotCode}`} value={pick[row._id]?.evidenceId || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], evidenceId: event.target.value } }))}>
                      <option value="">Select</option>
                      {(sheet.documents || []).map((doc) => <option key={`evidence-${doc._id}`} value={doc._id}>{doc.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <Button type="submit" size="sm">Select person</Button>
                {row.proposal?._id ? (
                  <div className="mt-3">
                    <div className="bg-register__grid">
                      <Form.Group className="bg-register__span-4">
                        <Form.Label>Consent evidence</Form.Label>
                        <Form.Select aria-label={`Consent for ${row.slotCode}`} value={pick[row._id]?.consentDocumentId || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], consentDocumentId: event.target.value } }))}>
                          <option value="">Select</option>
                          {(sheet.documents || []).filter((doc) => doc.accessClassification === 'restricted' || doc.accessClassification === 'commercial_confidential').map((doc) => (
                            <option key={`consent-${doc._id}`} value={doc._id}>{doc.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="bg-register__span-4">
                        <Form.Label>Valid from</Form.Label>
                        <Form.Control type="date" aria-label={`Consent from ${row.slotCode}`} value={pick[row._id]?.validFrom || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], validFrom: event.target.value } }))} />
                      </Form.Group>
                      <Form.Group className="bg-register__span-4">
                        <Form.Label>Valid to</Form.Label>
                        <Form.Control type="date" aria-label={`Consent to ${row.slotCode}`} value={pick[row._id]?.validTo || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], validTo: event.target.value } }))} />
                      </Form.Group>
                      <Form.Group className="bg-register__span-8">
                        <Form.Label>Override reason</Form.Label>
                        <Form.Control aria-label={`Override for ${row.slotCode}`} value={pick[row._id]?.overrideReason || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], overrideReason: event.target.value } }))} />
                      </Form.Group>
                    </div>
                    <Button size="sm" type="button" onClick={() => recordConsent(row.proposal._id, row._id)}>Record consent</Button>{' '}
                    <Button size="sm" type="button" variant="outline-secondary" onClick={() => reviewConsent(row.proposal._id)}>Review consent</Button>{' '}
                    <Button size="sm" type="button" variant="outline-secondary" onClick={() => override(row.proposal._id, row._id)}>Record override</Button>
                    <div className="bg-register__grid mt-3">
                      <Form.Group className="bg-register__span-3">
                        <Form.Label>Qualification result</Form.Label>
                        <Form.Select aria-label={`Qualification result for ${row.slotCode}`} value={pick[row._id]?.qualificationResult || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], qualificationResult: event.target.value } }))}>
                          <option value="">Select</option>
                          <option value="meets">Meets</option>
                          <option value="does_not_meet">Does not meet</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="bg-register__span-5">
                        <Form.Label>Qualification comment</Form.Label>
                        <Form.Control aria-label={`Qualification comment for ${row.slotCode}`} value={pick[row._id]?.qualificationComments || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], qualificationComments: event.target.value } }))} />
                      </Form.Group>
                      <Form.Group className="bg-register__span-4">
                        <Form.Label>Qualification evidence</Form.Label>
                        <Form.Select aria-label={`Qualification evidence for ${row.slotCode}`} value={pick[row._id]?.qualificationEvidence || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], qualificationEvidence: event.target.value } }))}>
                          <option value="">Select</option>
                          {(sheet.documents || []).map((doc) => <option key={`q-${doc._id}`} value={doc._id}>{doc.name}</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="bg-register__span-3">
                        <Form.Label>Experience result</Form.Label>
                        <Form.Select aria-label={`Experience result for ${row.slotCode}`} value={pick[row._id]?.experienceResult || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], experienceResult: event.target.value } }))}>
                          <option value="">Select</option>
                          <option value="meets">Meets</option>
                          <option value="does_not_meet">Does not meet</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="bg-register__span-5">
                        <Form.Label>Experience comment</Form.Label>
                        <Form.Control aria-label={`Experience comment for ${row.slotCode}`} value={pick[row._id]?.experienceComments || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], experienceComments: event.target.value } }))} />
                      </Form.Group>
                      <Form.Group className="bg-register__span-4">
                        <Form.Label>Experience evidence</Form.Label>
                        <Form.Select aria-label={`Experience evidence for ${row.slotCode}`} value={pick[row._id]?.experienceEvidence || ''} onChange={(event) => setPick((current) => ({ ...current, [row._id]: { ...current[row._id], experienceEvidence: event.target.value } }))}>
                          <option value="">Select</option>
                          {(sheet.documents || []).map((doc) => <option key={`e-${doc._id}`} value={doc._id}>{doc.name}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <Button size="sm" type="button" variant="outline-secondary" onClick={() => assess(row.proposal._id, row._id, 'qualification')}>Record qualification assessment</Button>{' '}
                    <Button size="sm" type="button" variant="outline-secondary" onClick={() => assess(row.proposal._id, row._id, 'experience')}>Record experience assessment</Button>
                  </div>
                ) : null}
              </section>
            </form>
          ))}
          {canSubmit ? <Button size="sm" type="button" onClick={submitTeam}>Submit team</Button> : null}
        </>
      ) : <p className="text-muted">You can read this sheet. Editing it needs team assignment permission.</p>}
      {sheet.canApprove ? (
        <form className="bg-register__form" onSubmit={(event) => event.preventDefault()}>
          <section>
            <h2>Team decision</h2>
            <Form.Group>
              <Form.Label>Comment</Form.Label>
              <Form.Control aria-label="Team decision comment" value={decisionComment} onChange={(event) => setDecisionComment(event.target.value)} />
            </Form.Group>
            <Button size="sm" type="button" onClick={() => decide('approve')}>Approve team</Button>{' '}
            <Button size="sm" type="button" variant="outline-secondary" onClick={() => decide('return')}>Return team</Button>
          </section>
        </form>
      ) : null}
    </div>
  );
};

export default TeamResourcesPanel;
